import { put, select, takeEvery } from 'redux-saga/effects';

import type { QcsSyncResourcesConfigProps } from 'app-components/flux/core/resourceStoreTypes';

import { resourceSagaUtils, resourceStoreTypes } from '../core';
import { featureFlagsSelectors } from '../feature-flags';
import * as accountsActions from './accountsActions';
import { getLastSyncDate } from './accountsSelectors';
import * as accountsTransformers from './accountsTransformers';
import type { Account, AccountProps } from './accountsTypes';

const mkQcsSyncAccountsConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<AccountProps>();

const mkResourceConfig = (props: Partial<QcsSyncResourcesConfigProps<AccountProps>>) =>
  mkQcsSyncAccountsConfig({
    resourceName: 'account',
    resourceBaseUrl: '/accounts',
    getLastSyncDate,

    transformResponseToResources: accountsTransformers.transformResponseToAccounts,
    transformResourceToRequestData: accountsTransformers.transformAccountToRequestData,
    transformResourcesToRequestData: accountsTransformers.transformAccountsToRequestData,

    ...props,
  });

export function* getAccountsActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: accountsActions.getAccountsSuccess,
    failureAction: accountsActions.getAccountsFailure,
  });
  yield resourceSagaUtils.takeAndDebounce(
    accountsActions.getAccounts,
    resourceSagaUtils.qcsSyncGetResources<AccountProps>,
    resourceConfig,
  );
}

export function* refreshAccountBalanceWatcher() {
  const resourceConfig = mkResourceConfig({
    resourceBaseUrl: '/transactions/refresh-account-balance',
    successAction: accountsActions.refreshAccountBalanceSuccess,
    failureAction: accountsActions.refreshAccountBalanceFailure,
    transformResourceWithResponse: (resource, response) =>
      'merge' in resource ? resource.merge(response.data as AccountProps) : resource,
  });
  yield takeEvery(
    accountsActions.refreshAccountBalance.type,
    resourceSagaUtils.qcsSyncCreateResource<AccountProps>,
    resourceConfig,
  );
}

export function* createAccountActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: accountsActions.createAccountSuccess,
    failureAction: accountsActions.createAccountFailure,
  });
  yield takeEvery(
    accountsActions.createAccount.type,
    resourceSagaUtils.qcsSyncCreateResource<AccountProps>,
    resourceConfig,
  );
}

export function* updateAccountActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: accountsActions.updateAccountSuccess,
    failureAction: accountsActions.updateAccountFailure,
  });
  yield takeEvery(
    accountsActions.updateAccount.type,
    resourceSagaUtils.qcsSyncUpdateResource<AccountProps>,
    resourceConfig,
  );
}

export function* deleteAccountActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: accountsActions.deleteAccountSuccess,
    failureAction: accountsActions.deleteAccountFailure,
  });
  yield takeEvery(
    accountsActions.deleteAccount.type,
    resourceSagaUtils.qcsSyncDeleteResource<AccountProps>,
    resourceConfig,
  );
}

export function* batchAccountsActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: accountsActions.batchAccountsSuccess,
    failureAction: accountsActions.batchAccountsFailure,
  });
  yield takeEvery(
    accountsActions.batchAccounts.type,
    resourceSagaUtils.qcsSyncUpdateResources<AccountProps>,
    resourceConfig,
  );
}

// =================================================================================================
// Account Actions
// =================================================================================================

//   id: accountId to impact
//   action: 'close' || 'disconnect' || 'make-manual'
export function* performAccountActionWatcher() {
  const config = mkQcsSyncAccountsConfig({
    resourceName: 'accounts',
    resourceBaseUrl: (action: ReturnType<typeof accountsActions.performAccountAction>) =>
      `/accounts/${action.payload.id}/${action.payload.qcsAction}`,
    successAction: accountsActions.performAccountActionSuccess,
    failureAction: accountsActions.performAccountActionFailure,
    transformResourceToRequestData: () => undefined, // no request body
    transformResourceWithResponse: (_resource, response) =>
      response.data?.resource ? accountsTransformers.transformQcsAccountToAccount(response.data.resource) : undefined,
  });
  yield takeEvery(
    accountsActions.performAccountAction.type,
    resourceSagaUtils.qcsSyncUpdateResource<AccountProps>,
    config,
  );
}

const { callFirst, callLast } = resourceSagaUtils.createGreedDebounce('accountsDumpUpdater');
export function* accountsDumpUpdater(resources: Array<Account>) {
  const bumpAccounts: boolean = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpAccounts');
  if (bumpAccounts) {
    yield callFirst(accountsDumpUpdaterFull, resources);
    yield callLast(accountsNudgeUpdater);
  }
}

function* accountsDumpUpdaterFull(resources: Array<Account>) {
  yield put(
    accountsActions.applyAccountsChanges(accountsTransformers.transformQcsAccountsToAccounts(resources), {
      context: 'BUMP_CHANGE_DUMP',
      props: { doMerge: false },
    }),
  );
}

export function* accountsNudgeUpdater() {
  const bumpAccounts: boolean = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpAccounts');
  if (bumpAccounts) {
    yield put(accountsActions.getAccounts(undefined, { context: 'BUMP_CHANGE_NUDGE' }));
  }
}

function* getAccountsSuccessActionWatcher() {
  yield takeEvery(accountsActions.getAccountsSuccess, function* getAccountsSuccess({ payload }) {
    yield put(
      accountsActions.applyAccountsChanges(payload.resources, {
        context: 'getAccountsSuccess',
        props: { doMerge: false },
      }),
    );
  });
}

function* createAccountSuccessActionWatcher() {
  yield takeEvery(accountsActions.createAccountSuccess, function* createAccountSuccess({ payload }) {
    yield put(accountsActions.applyAccountsChanges([payload], { context: 'createAccountSuccess' }));
  });
}

function* updateAccountSuccessActionWatcher() {
  yield takeEvery(accountsActions.updateAccountSuccess, function* updateAccountSuccess({ payload }) {
    yield put(accountsActions.applyAccountsChanges([payload], { context: 'updateAccountSuccess' }));
  });
}

function* deleteAccountSuccessActionWatcher() {
  yield takeEvery(accountsActions.deleteAccountSuccess, function* deleteAccountSuccess({ payload }) {
    yield put(accountsActions.applyAccountsChanges([payload], { context: 'deleteAccountSuccess' }));
  });
}

function* batchAccountsSuccessActionWatcher() {
  yield takeEvery(accountsActions.batchAccountsSuccess, function* batchAccountsSuccess({ payload }) {
    yield put(accountsActions.applyAccountsChanges(payload, { context: 'batchAccountsSuccess' }));
  });
}

function* performAccountActionSuccessActionWatcher() {
  yield takeEvery(
    accountsActions.performAccountActionSuccess,
    function* performAccountActionSuccess({ payload }: { payload: Account }) {
      yield put(
        accountsActions.applyAccountsChanges([payload], {
          context: 'performAccountActionSuccess',
          props: { doMerge: false },
        }),
      );
    },
  );
}

export const accountsSagasObj = {
  getAccountsActionWatcher,
  getAccountsSuccessActionWatcher,
  createAccountActionWatcher,
  createAccountSuccessActionWatcher,
  updateAccountActionWatcher,
  updateAccountSuccessActionWatcher,
  deleteAccountActionWatcher,
  deleteAccountSuccessActionWatcher,
  batchAccountsActionWatcher,
  batchAccountsSuccessActionWatcher,
  performAccountActionWatcher,
  performAccountActionSuccessActionWatcher,
  refreshAccountBalanceWatcher,
};

export default Object.values(accountsSagasObj);
